import jsPDF from 'jspdf';
import { BRAND_MARK, BRAND_WORDMARK } from './brand';
import { BRANDING, brandingFooter } from './branding';
import { BRAND_RGB, BRAND_LIGHT_RGB, BRAND_BORDER_RGB } from './brandColors';

export const A4_PAGE_WIDTH = 210;
export const A4_PAGE_HEIGHT = 297;
export const A4_MARGIN = 20;

/**
 * Colori della carta intestata.
 *
 * Si chiamavano TEAL_* ed erano il verde acqua dell'azienda precedente: era
 * il colore piu' visibile al cliente finale, perche' finisce su ogni fattura
 * e ogni offerta stampata. Ora derivano dalla palette di marca.
 */
export const BRAND_PDF_RGB: [number, number, number] = BRAND_RGB;
export const BRAND_PDF_LIGHT_RGB: [number, number, number] = BRAND_LIGHT_RGB;
export const BRAND_PDF_BORDER_RGB: [number, number, number] = BRAND_BORDER_RGB;

export interface CompanyInfo {
  ragioneSociale: string;
  indirizzo: string;
  cap: string;
  citta: string;
  provincia: string;
  partitaIva: string;
  codiceFiscale: string;
  rea: string;
  regimeFiscale: string;
  regimeFiscaleCode: string;
  email: string;
  pec: string;
  telefono: string;
  sito: string;
  iban: string;
  banca: string;
}

/**
 * Anagrafica dell'azienda, ricavata da `branding.ts`. Prima era scritta a mano
 * qui: la forma dell'oggetto resta identica perche' e' letta da una quarantina
 * di punti fra stampe, fatture e modali.
 *
 * Ogni campo e' un `get`, non un valore copiato: cosi' legge `BRANDING` nel
 * momento in cui serve. Con una copia, i dati salvati in Impostazioni sarebbero
 * arrivati troppo tardi e le fatture avrebbero continuato a uscire con la
 * vecchia sede finche' qualcuno non ricaricava la pagina.
 */
export const COMPANY_INFO: CompanyInfo = {
  get ragioneSociale() { return BRANDING.ragioneSociale; },
  get indirizzo() { return BRANDING.indirizzo; },
  get cap() { return BRANDING.cap; },
  get citta() { return BRANDING.citta; },
  get provincia() { return BRANDING.provincia; },
  get partitaIva() { return BRANDING.partitaIva; },
  get codiceFiscale() { return BRANDING.codiceFiscale; },
  get rea() { return BRANDING.rea; },
  get regimeFiscale() { return BRANDING.regimeFiscale; },
  get regimeFiscaleCode() { return BRANDING.regimeFiscaleCode; },
  get email() { return BRANDING.email; },
  get pec() { return BRANDING.pec; },
  get telefono() { return BRANDING.telefono; },
  get sito() { return BRANDING.sito; },
  get iban() { return BRANDING.iban; },
  get banca() { return BRANDING.banca; },
};

/** Pie' di pagina predefinito. E' una funzione perche' i dati azienda
 * possono cambiare dalla pagina Impostazioni dopo l'avvio dell'app. */
export const companyFooterDefault = (): string => brandingFooter();

export const MARCHIO_PUBLIC_PATH = BRAND_MARK;
export const LOGOTIPO_PUBLIC_PATH = BRAND_WORDMARK;

export interface LetterheadCustomer {
  company_name?: string | null;
  contact_person?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  province?: string | null;
  vat_number?: string | null;
  tax_code?: string | null;
  phone?: string | null;
  [key: string]: unknown;
}

/** Carica un'immagine pubblica come data URL (base64) per jsPDF.addImage. */
export const loadImageAsDataUrl = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Impossibile caricare: ${url}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Carica un'immagine e la ridimensiona a `maxWidthPx` (lato lungo) prima di
 * restituirla come PNG data URL. Le immagini di origine (es. marchio 688×697px,
 * ~640KB) vengono incorporate da jsPDF a piena risoluzione anche se mostrate a
 * pochi mm: scalarle alla dimensione reale di stampa riduce il peso del PDF da
 * megabyte a pochi KB. Mantiene la trasparenza (PNG).
 */
export const loadImageScaledDataUrl = async (
  url: string,
  maxWidthPx: number
): Promise<string> => {
  const original = await loadImageAsDataUrl(url);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidthPx / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(original); // fallback: meglio un PDF grande che nessun PDF
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = original;
  });
};

/**
 * Disegna il logotipo e la linea di separazione in cima alla pagina.
 *
 * Prima disegnava due immagini incolonnate: un marchio quadrato di 18mm e
 * sotto il logotipo esteso. Erano due varianti dello stesso marchio
 * dell'azienda precedente, e funzionava finche' le due immagini erano davvero
 * diverse. Il logo SLAB e'
 * uno solo, orizzontale: ripeterlo due volte darebbe una carta intestata con
 * lo stesso logo stampato due volte. Ne resta uno, piu' grande.
 *
 * Il primo parametro e' tenuto per non cambiare la firma nei punti che la
 * chiamano, ma non viene piu' disegnato.
 */
export const drawLetterhead = (
  doc: jsPDF,
  _marchioDataUrl: string,
  logotipoDataUrl: string,
  pageWidth: number = A4_PAGE_WIDTH,
  margin: number = A4_MARGIN
): void => {
  const logoProps = doc.getImageProperties(logotipoDataUrl);
  const logoW = 62;
  const logoH = (logoProps.height / logoProps.width) * logoW;
  const logoY = 10;
  doc.addImage(logotipoDataUrl, 'PNG', (pageWidth - logoW) / 2, logoY, logoW, logoH);

  const lineY = logoY + logoH + 4;
  doc.setDrawColor(...BRAND_PDF_RGB);
  doc.setLineWidth(0.5);
  doc.line(margin, lineY, pageWidth - margin, lineY);
};

/**
 * Disegna box "Dati Emittente" (sinistra) + "Spett.le Cliente" (destra) affiancati.
 * Ritorna la nuova yPos sotto i due box.
 */
export const drawIssuerAndCustomerBoxes = (
  doc: jsPDF,
  customer: LetterheadCustomer | undefined | null,
  yStart: number = 48,
  pageWidth: number = A4_PAGE_WIDTH,
  margin: number = A4_MARGIN
): number => {
  const gap = 4;
  const boxW = (pageWidth - 2 * margin - gap) / 2;
  const boxH = 52;

  // Dati Emittente (sinistra)
  doc.setFillColor(...BRAND_PDF_LIGHT_RGB);
  doc.setDrawColor(...BRAND_PDF_BORDER_RGB);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, yStart, boxW, boxH, 3, 3, 'FD');

  let yL = yStart + 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_PDF_RGB);
  doc.text('DATI EMITTENTE', margin + 3, yL);

  yL += 4;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text(COMPANY_INFO.ragioneSociale, margin + 3, yL);

  yL += 4;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);
  const issuerLines = [
    COMPANY_INFO.indirizzo,
    `${COMPANY_INFO.cap} ${COMPANY_INFO.citta} (${COMPANY_INFO.provincia})`,
    `P.IVA: ${COMPANY_INFO.partitaIva}`,
    `C.F.: ${COMPANY_INFO.codiceFiscale}`,
    `REA: ${COMPANY_INFO.rea}`,
    `Regime fiscale: ${COMPANY_INFO.regimeFiscaleCode} - ${COMPANY_INFO.regimeFiscale}`,
    `PEC: ${COMPANY_INFO.pec}`,
    `Email: ${COMPANY_INFO.email}  Tel: ${COMPANY_INFO.telefono}`,
  ];
  issuerLines.forEach((line) => {
    doc.text(line, margin + 3, yL);
    yL += 3.6;
  });

  // Spett.le Cliente (destra)
  const xR = margin + boxW + gap;
  doc.setFillColor(...BRAND_PDF_LIGHT_RGB);
  doc.setDrawColor(...BRAND_PDF_BORDER_RGB);
  doc.roundedRect(xR, yStart, boxW, boxH, 3, 3, 'FD');

  let yR = yStart + 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_PDF_RGB);
  doc.text('SPETT.LE CLIENTE', xR + 3, yR);

  yR += 4;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text(customer?.company_name || 'Cliente', xR + 3, yR);

  yR += 4;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);

  const cityLine = [customer?.postal_code, customer?.city, customer?.province ? `(${customer.province})` : null]
    .filter(Boolean)
    .join(' ');

  const custLines = [
    customer?.address,
    cityLine || null,
    customer?.vat_number ? `P.IVA: ${customer.vat_number}` : null,
    customer?.tax_code ? `C.F.: ${customer.tax_code}` : null,
    customer?.email ? `Email: ${customer.email}` : null,
    customer?.phone ? `Tel: ${customer.phone}` : null,
  ].filter(Boolean) as string[];

  custLines.forEach((line) => {
    doc.text(line, xR + 3, yR);
    yR += 3.6;
  });

  return yStart + boxH + 6;
};

/**
 * Disegna box "Spett.le {azienda}" con indirizzo + P.IVA. Ritorna la nuova yPos.
 * yStart consigliato: 48 (subito sotto la linea letterhead).
 * @deprecated Usa drawIssuerAndCustomerBoxes per fatture a norma di legge.
 */
export const drawCustomerBox = (
  doc: jsPDF,
  customer: LetterheadCustomer | undefined | null,
  yStart: number = 48,
  pageWidth: number = A4_PAGE_WIDTH,
  margin: number = A4_MARGIN
): number => {
  const cityLine = [customer?.postal_code, customer?.city, customer?.province].filter(Boolean).join(' ');
  const detailLines = [
    customer?.address || null,
    cityLine || null,
    customer?.vat_number ? `P.IVA: ${customer.vat_number}` : null,
    customer?.tax_code ? `C.F.: ${customer.tax_code}` : null,
    customer?.email ? `Email: ${customer.email}` : null,
    customer?.phone ? `Tel.: ${customer.phone}` : null,
    customer?.contact_person ? `Referente: ${customer.contact_person}` : null,
  ].filter(Boolean) as string[];

  const topPad = 7;
  const titleToDetailGap = 5;
  const lineGap = 4.5;
  const bottomPad = 4;
  const boxH = topPad + titleToDetailGap + detailLines.length * lineGap + bottomPad;

  doc.setFillColor(...BRAND_PDF_LIGHT_RGB);
  doc.setDrawColor(...BRAND_PDF_BORDER_RGB);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, yStart, pageWidth - 2 * margin, boxH, 3, 3, 'FD');

  let yPos = yStart + topPad;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  const companyName = `Spett.le ${customer?.company_name || 'Cliente'}`;
  doc.text(companyName, pageWidth / 2, yPos, { align: 'center' });

  yPos += titleToDetailGap;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);

  detailLines.forEach((line) => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += lineGap;
  });

  return yStart + boxH + 8;
};

/** Disegna footer aziendale (multi-linea, dati legali completi) + linea separatrice. */
export const drawPageFooter = (
  doc: jsPDF,
  _companyFooter: string = companyFooterDefault(),
  pageWidth: number = A4_PAGE_WIDTH,
  pageHeight: number = A4_PAGE_HEIGHT,
  margin: number = A4_MARGIN
): void => {
  const line1 = `${COMPANY_INFO.ragioneSociale}  •  ${COMPANY_INFO.indirizzo}, ${COMPANY_INFO.cap} ${COMPANY_INFO.citta} (${COMPANY_INFO.provincia})`;
  const line2 = `P.IVA ${COMPANY_INFO.partitaIva}  •  C.F. ${COMPANY_INFO.codiceFiscale}  •  REA ${COMPANY_INFO.rea}  •  ${COMPANY_INFO.regimeFiscaleCode} ${COMPANY_INFO.regimeFiscale}`;
  const line3 = `${COMPANY_INFO.sito}  •  ${COMPANY_INFO.email}  •  PEC ${COMPANY_INFO.pec}  •  Tel ${COMPANY_INFO.telefono}`;

  const baseY = pageHeight - 14;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, baseY - 2, pageWidth - margin, baseY - 2);

  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(110, 110, 110);
  doc.text(line1, pageWidth / 2, baseY + 2, { align: 'center' });
  doc.text(line2, pageWidth / 2, baseY + 5.5, { align: 'center' });
  doc.text(line3, pageWidth / 2, baseY + 9, { align: 'center' });
};
